import { CommonPage, Content, LoadableContent, InRow } from "common/Grid";
import { PrimaryButton, SecondaryButton } from "common/Forms";
import { RequestSiteConfigForm } from "../Common/RequestSiteConfigForm";
import { parseRequestSiteConfigErrors } from "../Common/requestSiteConfig.schema";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useCreateSalesLeadRequestDemoMutation,
  useUpdateSalesLeadRequestDemoMutation,
  useCreateSalesLeadRequestLiveMutation,
  useUpdateSalesLeadRequestLiveMutation,
  useGetSalesLeadsListQuery,
} from "store/action/salesLeadsApi";
import { useEffect, useRef, useMemo, useState } from "react";
import { captureComponentError } from "utils/sentry";
import { useGetFileUploadMutation } from "store/action/inquiryApi";
import { siteRequestStatus } from "Constant/Constant";

const generateInStoreLoyaltyId = (len = 25) =>
  Array.from(
    { length: len },
    () => "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 52)],
  ).join("");

// Every field the form can edit is pre-populated with "" (or its own default) so the key
// always exists on userInput, even if the user never touches it — otherwise a masked/unset
// field's key would be missing from the object entirely and get dropped from the payload.
const defaultConfig = {
  squareConfig: {
    accessToken: "",
    appFeeAllowed: false,
    appFeePercentage: "",
    appId: "",
    appSecret: "",
    basePath: "",
    country: "",
    environment: "",
    expiresAt: "",
    isRestaurantMenu: false,
    locationId: "",
    merchantId: "",
    redirectUri: "",
    refreshToken: "",
  },
  triniteq: {
    client_id: "",
    client_secret: "",
    grant_type: "",
    org_id: "",
    outletID: "",
    providerID: "",
    providerName: "",
    scope: "",
    signature: "",
  },
  webhook: {
    notificationUrl: "",
    signatureKey: "",
  },
  appConfig: {
    inStoreLoyaltyId: generateInStoreLoyaltyId(),
  },
  mailSettings: {
    mailPort: "",
    password: "",
    resetPasswordLink: "",
    service: "",
    userName: "",
  },
  tevalis: {
    CompanyId: "",
    DEVID: "",
    GUID: "",
    GUID2: "",
    siteId: "",
  },
  twilio: {
    accountSid: "",
    apiKeySecret: "",
    from: "",
    apiKeySid: "",
  },
  oneSignal: {
    apiKey: "",
    appId: "",
    staffApiKey: "",
    staffAppId: "",
  },
  payStack: {
    apiUrl: "",
    feeMoney: "",
    secretKey: "",
    subAccount: "",
  },
  cyberSourceConfig: {
    apiKeyId: "",
    currency: "",
    environment: "",
    merchantId: "",
    secretKey: "",
  },
  stripeConfig: {
    connectedAccountId: "",
    country: "",
    feeMoney: "",
    isProcessingFeeEnabled: false,
    processingFeePercentage: "",
    publishableKey: "",
    secretKey: "",
    taxCode: "",
  },
  clientName: "",
  filePwd: "",
  reportUrl: "",
  paymentProvider: "stripe",
  locationId: "",
  franchiseid: "",
  locale: "",
  squareSync: false,
  triniteqPos: false,
  tevalisPos: false,
  siteid: "",
};

// A flat `{ ...defaultConfig, ...savedData }` spread would let a saved config's nested
// object (e.g. squareConfig: { accessToken: "..." }) wholesale replace defaultConfig's
// version and drop any keys the saved data doesn't have. Merge one level into known
// nested config objects so every key from defaultConfig always survives.
const NESTED_CONFIG_KEYS = [
  "squareConfig", "triniteq", "webhook", "appConfig", "mailSettings",
  "tevalis", "twilio", "oneSignal", "payStack", "cyberSourceConfig", "stripeConfig",
];

const mergeWithDefaults = (savedData, extra = {}) => {
  const merged = { ...defaultConfig, ...savedData, ...extra };
  for (const key of NESTED_CONFIG_KEYS) {
    merged[key] = { ...defaultConfig[key], ...savedData?.[key], ...extra?.[key] };
  }
  return merged;
};

const getFileType = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") return "png";
  const mime = dataUrl.split(";")[0].split(":")[1] || "";
  if (mime.includes("icon") || mime.includes("x-icon") || mime.includes("vnd.microsoft.icon")) return "ico";
  if (mime.includes("jpeg")) return "jpg";
  return mime.split("/")[1] || "png";
};

const generateHexColorVariants = (inputColor) => {
  if (!inputColor || typeof inputColor !== "string") return { hoverColor: "", disabledColor: "" };

  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));

  const rgbToHex = (r, g, b) =>
    `#${[r, g, b]
      .map((v) => clamp(v).toString(16).padStart(2, "0"))
      .join("")}`;

  const parseColor = (color) => {
    color = color.trim();

    // HEX (#fff | #ffffff)
    if (color.startsWith("#")) {
      let hex = color.slice(1);
      if (hex.length === 3) {
        hex = hex.split("").map((c) => c + c).join("");
      }
      const num = parseInt(hex, 16);
      return {
        r: (num >> 16) & 255,
        g: (num >> 8) & 255,
        b: num & 255,
      };
    }

    // RGB / RGBA
    const match = color.match(/rgba?\(([^)]+)\)/);
    if (match) {
      const [r, g, b] = match[1].split(",").map((v) => parseFloat(v));
      return { r, g, b };
    }

    throw new Error("Unsupported color format");
  };

  try {
    const { r, g, b } = parseColor(inputColor);

    return {
      mainColor: rgbToHex(r, g, b),
      hoverColor: rgbToHex(r - 20, g - 20, b - 20),
      disabledColor: rgbToHex(r + 40, g + 40, b + 40),
    };
  } catch (error) {
    return { hoverColor: "", disabledColor: "" };
  }
};

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const uploadSiteFiles = async (siteData, uploadFile) => {
  let appIconUrl = siteData?.appIcon?.url || siteData?.appIcon;
  if (typeof appIconUrl === "string" && appIconUrl.startsWith("data:")) {
    const res = await uploadFile({ base64: appIconUrl, type: getFileType(appIconUrl) }).unwrap();
    if (res?.status === "SUCCESS") appIconUrl = res.data?.url || res.data;
    else throw new Error("App icon upload failed");
  }

  let faviconUrl = siteData?.favicon?.url || siteData?.favicon;
  if (typeof faviconUrl === "string" && faviconUrl.startsWith("data:")) {
    const res = await uploadFile({ base64: faviconUrl, type: getFileType(faviconUrl) }).unwrap();
    if (res?.status === "SUCCESS") faviconUrl = res.data?.url || res.data;
    else throw new Error("Favicon upload failed");
  }

  let siteBannerUrl = siteData?.appConfig?.siteBanner?.url || siteData?.appConfig?.siteBanner;
  if (typeof siteBannerUrl === "string" && siteBannerUrl.startsWith("data:")) {
    const res = await uploadFile({ base64: siteBannerUrl, type: getFileType(siteBannerUrl) }).unwrap();
    if (res?.status === "SUCCESS") siteBannerUrl = res.data?.url || res.data;
    else throw new Error("Site banner upload failed");
  }

  const primaryColor = siteData?.appConfig?.colors?.primaryColor;
  const variants = generateHexColorVariants(primaryColor);

  return {
    ...siteData,
    appIcon: appIconUrl,
    favicon: faviconUrl,
    appConfig: {
      ...siteData.appConfig,
      siteBanner: siteBannerUrl,
      colors: {
        ...siteData.appConfig?.colors,
        primaryColorHover: variants.hoverColor,
        primaryColorDisable: variants.disabledColor,
      },
    },
  };
};

// Sensitive fields (secrets/tokens) skip client-side required validation and are
// validated server-side instead; map the backend's rejected keys back onto the form
// the same way a required-field error would show.
const buildInvalidKeyErrors = (invalidKeys, message) =>
  Object.fromEntries(invalidKeys.map((key) => [key, message || "Invalid value"]));

// The rejection can come back as a flat `invalidKeys` array, or nested per-site under
// `invalidSites: [{ _id, invalidKeys }]` — pull whichever shape is present.
const extractInvalidKeys = (data) => {
  if (Array.isArray(data?.invalidKeys)) return data.invalidKeys;
  if (Array.isArray(data?.invalidSites)) {
    return data.invalidSites.flatMap((site) => (Array.isArray(site?.invalidKeys) ? site.invalidKeys : []));
  }
  return [];
};

// `invalidSites` only lists the sites that actually failed (sparse, not one entry per
// submitted site), each identified by its email/siteName rather than array position — so
// entries can NOT be matched to tabs by index. Match each entry back to the site it came
// from via its email (appConfig.Email) or, failing that, its top-level SiteName.
const extractInvalidKeysPerSite = (data, sites) => {
  const siteCount = sites.length;
  if (Array.isArray(data?.invalidSites)) {
    return sites.map((site) => {
      const match = data.invalidSites.find((s) => {
        if (site?.appConfig?.Email && s?.email) return s.email === site.appConfig.Email;
        if (site?.SiteName && s?.siteName) return s.siteName === site.SiteName;
        return false;
      });
      return Array.isArray(match?.invalidKeys) ? match.invalidKeys : [];
    });
  }
  // A flat invalidKeys array has no per-site info — surface it on every tab so it isn't lost.
  if (Array.isArray(data?.invalidKeys)) {
    return Array.from({ length: siteCount }, () => data.invalidKeys);
  }
  return Array.from({ length: siteCount }, () => []);
};

// Missing fields are sent through as "" (like Configuration/Common/MainForm.jsx's save
// flow) instead of being dropped from the payload.
const cleanObject = (obj) => {
  if (obj === null || obj === undefined) return "";
  if (typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(cleanObject);
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, cleanObject(v)]));
};

const SingleRequestForm = ({ lead, type, isFranchise, index, isActive, classNames = [] }) => {
  const navigate = useNavigate();
  const successRef = useRef(false);

  useEffect(() => {
    return () => {
      if (!successRef.current) {
        toast.dismiss();
      }
    };
  }, []);

  const isDemo = type === "demo";
  const isLive = type === "live";

  const isEdit = useMemo(() => {
    const status = isDemo ? lead?.requestDemoStatus : lead?.requestLiveStatus;
    return status !== siteRequestStatus.NOT_REQUESTED;
  }, [isDemo, lead?.requestDemoStatus, lead?.requestLiveStatus]);

  const existingData = isDemo ? lead?.requestDemo : isLive ? lead?.requestLive : null;
  const dataToUse = Array.isArray(existingData) ? existingData[index] : existingData;

  const prefillData = useMemo(() => {
    if (isLive && !dataToUse && lead?.requestDemoStatus !== siteRequestStatus.NOT_REQUESTED) {
      return Array.isArray(lead?.requestDemo) ? lead?.requestDemo[index] : lead?.requestDemo;
    }
    return null;
  }, [isLive, dataToUse, lead, index]);

  const [userInput, setUserInput] = useState(
    dataToUse || prefillData
      ? mergeWithDefaults(dataToUse || prefillData)
      : mergeWithDefaults(null, { SiteName: isFranchise ? `${lead?.name || ""} - Site ${index + 1}` : "" }),
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [apiErrors, setApiErrors] = useState({});

  const [createRequestDemo, { isLoading: isCreatingDemo }] = useCreateSalesLeadRequestDemoMutation();
  const [updateRequestDemo, { isLoading: isUpdatingDemo }] = useUpdateSalesLeadRequestDemoMutation();
  const [createRequestLive, { isLoading: isCreatingLive }] = useCreateSalesLeadRequestLiveMutation();
  const [updateRequestLive, { isLoading: isUpdatingLive }] = useUpdateSalesLeadRequestLiveMutation();
  const [uploadFile, { isLoading: uploadFileIsloading }] = useGetFileUploadMutation();

  const isLoading = pageLoading || isCreatingDemo || isUpdatingDemo || isCreatingLive || isUpdatingLive;

  const isDraftEnabled = !!userInput?.appConfig?.Email?.trim();

  const handleSaveDraft = async () => {
    const currentEmail = userInput?.appConfig?.Email?.trim();
    if (!currentEmail || !EMAIL_REGEX.test(currentEmail)) {
      toast.error("Please enter a valid email before saving as draft");
      return;
    }

    try {
      setPageLoading(true);
      setApiErrors({});

      const processedSite = await uploadSiteFiles(userInput, uploadFile);
      const { isDraft, ...cleanSite } = processedSite;

      const draftPayload = {
        salesLeadId: lead._id,
        isFranchise: !!isFranchise,
        sites: [cleanSite],
        isDraft: true,
      };
      const response = await (isEdit
        ? (isDemo ? updateRequestDemo : updateRequestLive)
        : (isDemo ? createRequestDemo : createRequestLive))({ body: cleanObject(draftPayload) }).unwrap();

      successRef.current = true;
      toast.success(response?.message || "Draft saved successfully");
      navigate("/sales-leads");
    } catch (error) {
      const invalidKeys = extractInvalidKeys(error?.data);
      if (Array.isArray(invalidKeys) && invalidKeys.length > 0) {
        setApiErrors(buildInvalidKeyErrors(invalidKeys, error?.data?.message));
      }
      captureComponentError(error, { component: "SingleRequestForm", action: "handleSaveDraft" });
      toast.error(error?.data?.message || "Something went wrong");
    } finally {
      setPageLoading(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitted(true);

    const selectedProvider = userInput?.paymentProvider || "stripe";
    const errors = parseRequestSiteConfigErrors(userInput, selectedProvider, !!isFranchise, true);
    if (Object.keys(errors).length > 0) {
      setTimeout(() => {
        const firstErrorField = Array.from(document.querySelectorAll(".has-errors")).find(
          (el) => el.offsetParent !== null,
        );
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 100);
      return;
    }

    try {
      setPageLoading(true);
      setApiErrors({});

      const processedSite = await uploadSiteFiles(userInput, uploadFile);
      const { isDraft, ...cleanSite } = processedSite;

      let response;

      if (isEdit) {
        const payload = { sites: [cleanSite] };
        if (isDemo) response = await updateRequestDemo({ body: cleanObject(payload) }).unwrap();
        else if (isLive) response = await updateRequestLive({ body: cleanObject(payload) }).unwrap();
      } else {
        const payload = { salesLeadId: lead._id, isFranchise: !!isFranchise, sites: [cleanSite] };
        if (isDemo) response = await createRequestDemo({ body: cleanObject(payload) }).unwrap();
        else if (isLive) response = await createRequestLive({ body: cleanObject(payload) }).unwrap();
      }

      successRef.current = true;
      toast.success(
        response?.message ||
        (isEdit ? `Request ${type} updated successfully!` : `Request ${type} submitted successfully`),
      );
      navigate("/sales-leads");
    } catch (error) {
      const invalidKeys = extractInvalidKeys(error?.data);
      if (Array.isArray(invalidKeys) && invalidKeys.length > 0) {
        setApiErrors(buildInvalidKeyErrors(invalidKeys, error?.data?.message));
      }
      captureComponentError(error, { component: "RequestFormPage", action: "handleSubmit" });
      toast.error(error?.data?.message || "Something went wrong");
    } finally {
      setPageLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/sales-leads");
  };

  return (
    <div className={[isActive ? "block" : "hidden", ...classNames].join(" ")}>
      <LoadableContent isLoading={isLoading}>
        <RequestSiteConfigForm
          userInput={userInput}
          uploadFileIsloading={uploadFileIsloading}
          setUserInput={setUserInput}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isFranchise={isFranchise}
          hideActions={true}
          externalIsSubmitted={isSubmitted}
          idPrefix={`tab-${index}-`}
          apiErrors={apiErrors}
          onUploadFile={async (base64, fileType) => {
            const res = await uploadFile({ base64, type: fileType }).unwrap();
            if (res?.status === "SUCCESS") return res.data?.url || res.data;
            throw new Error("Upload failed");
          }}
        />
        <InRow gap={1} classNames={["justify-center mt-8"]}>
          {!isEdit && (
            <SecondaryButton
              disabled={uploadFileIsloading || !isDraftEnabled}
              classNames={["min-w-[130px]"]}
              action={handleSaveDraft}>
              Save as Draft
            </SecondaryButton>
          )}
          <PrimaryButton disabled={uploadFileIsloading} classNames={["min-w-[130px]"]} action={handleSubmit}>
            Submit
          </PrimaryButton>
          <SecondaryButton
            disabled={uploadFileIsloading}
            classNames={["min-w-[130px]"]}
            action={handleCancel}>
            Cancel
          </SecondaryButton>
        </InRow>
      </LoadableContent>
    </div>
  );
};

const FranchiseMultiTabPage = ({ lead, type, franchiseCount, isFranchise }) => {
  const navigate = useNavigate();
  const successRef = useRef(false);

  const isDemo = type === "demo";
  const isLive = type === "live";

  const existingData = isDemo ? lead?.requestDemo : isLive ? lead?.requestLive : null;
  // const isEdit = Array.isArray(existingData) && existingData.length > 0;

  const isEdit = isDemo ? (lead.requestDemoStatus !== siteRequestStatus.NOT_REQUESTED) : (lead.requestLiveStatus !== siteRequestStatus.NOT_REQUESTED);

  const [sitesData, setSitesData] = useState(() =>
    Array.from({ length: franchiseCount }, (_, i) => {
      let d = Array.isArray(existingData) ? existingData[i] : null;

      if (!d && isLive && lead?.requestDemoStatus !== siteRequestStatus.NOT_REQUESTED) {
        d = Array.isArray(lead?.requestDemo) ? lead?.requestDemo[i] : (i === 0 ? lead?.requestDemo : null);
      }

      return d
        ? mergeWithDefaults(d)
        : mergeWithDefaults(null, { SiteName: `${lead?.name || ""} - Site ${i + 1}` });
    }),
  );

  const [currentTab, setCurrentTab] = useState(0);
  const [tabsSubmitted, setTabsSubmitted] = useState(Array(franchiseCount).fill(false));
  const [pageLoading, setPageLoading] = useState(false);
  const [apiErrorsByTab, setApiErrorsByTab] = useState(() => Array(franchiseCount).fill({}));

  const [createRequestDemo, { isLoading: isCreatingDemo }] = useCreateSalesLeadRequestDemoMutation();
  const [updateRequestDemo, { isLoading: isUpdatingDemo }] = useUpdateSalesLeadRequestDemoMutation();
  const [createRequestLive, { isLoading: isCreatingLive }] = useCreateSalesLeadRequestLiveMutation();
  const [updateRequestLive, { isLoading: isUpdatingLive }] = useUpdateSalesLeadRequestLiveMutation();
  const [uploadFile, { isLoading: uploadFileIsloading }] = useGetFileUploadMutation();

  const isLoading = pageLoading || isCreatingDemo || isUpdatingDemo || isCreatingLive || isUpdatingLive;

  useEffect(() => {
    return () => {
      if (!successRef.current) toast.dismiss();
    };
  }, []);

  const updateSiteData = (index, updater) => {
    setSitesData((prev) => {
      const next = [...prev];
      next[index] = typeof updater === "function" ? updater(prev[index]) : updater;
      return next;
    });
  };

  const validateTab = (index) => {
    const data = sitesData[index];
    const selectedProvider = data?.paymentProvider || "stripe";
    return parseRequestSiteConfigErrors(data, selectedProvider, true, index === 0);
  };

  const handleNext = () => {
    const next = [...tabsSubmitted];
    next[currentTab] = true;
    setTabsSubmitted(next);

    if (Object.keys(validateTab(currentTab)).length > 0) {
      setTimeout(() => {
        const firstErrorField = Array.from(document.querySelectorAll(".has-errors")).find(
          (el) => el.offsetParent !== null,
        );
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 100);
      return;
    }
    setCurrentTab((prev) => prev + 1);
  };

  const handlePrev = () => setCurrentTab((prev) => prev - 1);



  const isDraftEnabled = !!sitesData[currentTab]?.appConfig?.Email?.trim();

  const handleSaveDraft = async () => {
    const currentEmail = sitesData[currentTab]?.appConfig?.Email?.trim();
    if (!currentEmail || !EMAIL_REGEX.test(currentEmail)) {
      toast.error("Please enter a valid email before saving as draft");
      return;
    }

    // Track each filled site's original tab index so a rejection can be routed back to it.
    const filledTabIndexes = [];
    const filledSites = sitesData.filter((s, i) => {
      const keep = !!s?.appConfig?.Email?.trim();
      if (keep) filledTabIndexes.push(i);
      return keep;
    });

    try {
      setPageLoading(true);
      setApiErrorsByTab(Array(franchiseCount).fill({}));

      const firstSite = sitesData[0];
      const firstCompanyName = firstSite?.appConfig?.companyName || "";

      const sitesWithImages = filledSites.map((s) => ({
        ...s,
        favicon: s.favicon || firstSite.favicon,
        appConfig: { ...s.appConfig, companyName: firstCompanyName },
      }));

      const processedSites = await Promise.all(
        sitesWithImages.map((s) => uploadSiteFiles(s, uploadFile)),
      );

      const cleanSites = processedSites.map(({ isDraft, ...site }) => site);

      const draftPayload = { salesLeadId: lead._id, isFranchise: !!isFranchise, franchiseCount, sites: cleanSites, isDraft: true };

      const cleanedPayload = cleanObject(draftPayload);
      let response;
      if (isDemo) response = await createRequestDemo({ body: cleanedPayload }).unwrap();
      else if (isLive) response = await createRequestLive({ body: cleanedPayload }).unwrap();

      successRef.current = true;
      toast.success(response?.message || "Draft saved successfully");
      navigate("/sales-leads");
    } catch (error) {
      const perSiteKeys = extractInvalidKeysPerSite(error?.data, filledSites);
      const nextErrors = Array(franchiseCount).fill({});
      let firstErrorTab = -1;
      perSiteKeys.forEach((keys, filledIndex) => {
        if (keys.length === 0) return;
        const tabIndex = filledTabIndexes[filledIndex];
        nextErrors[tabIndex] = buildInvalidKeyErrors(keys, error?.data?.message);
        if (firstErrorTab === -1) firstErrorTab = tabIndex;
      });
      setApiErrorsByTab(nextErrors);
      if (firstErrorTab !== -1) setCurrentTab(firstErrorTab);
      captureComponentError(error, { component: "FranchiseMultiTabPage", action: "handleSaveDraft" });
      toast.error(error?.data?.message || "Something went wrong");
    } finally {
      setPageLoading(false);
    }
  };

  const handleSubmit = async () => {
    setTabsSubmitted(Array(franchiseCount).fill(true));

    if (Object.keys(validateTab(currentTab)).length > 0) {
      setTimeout(() => {
        const firstErrorField = Array.from(document.querySelectorAll(".has-errors")).find(
          (el) => el.offsetParent !== null,
        );
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 100);
      return;
    }

    try {
      setPageLoading(true);
      setApiErrorsByTab(Array(franchiseCount).fill({}));

      const firstSite = sitesData[0];
      const firstCompanyName = firstSite?.appConfig?.companyName || "";
      const sitesWithImages = sitesData.map((s, i) => ({
        ...s,
        favicon: i === 0 ? s.favicon : firstSite.favicon,
        appConfig: { ...s.appConfig, companyName: firstCompanyName },
      }));

      const processedSites = await Promise.all(
        sitesWithImages.map((s) => uploadSiteFiles(s, uploadFile)),
      );

      const cleanSites = processedSites.map(({ isDraft, ...site }) => site);

      let response;
      if (isEdit) {
        const payload = { sites: cleanSites };
        const cleanedPayload = cleanObject(payload);
        if (isDemo) response = await updateRequestDemo({ body: cleanedPayload }).unwrap();
        else if (isLive) response = await updateRequestLive({ body: cleanedPayload }).unwrap();
      } else {
        const payload = { salesLeadId: lead._id, isFranchise: true, sites: cleanSites };
        const cleanedPayload = cleanObject(payload);
        if (isDemo) response = await createRequestDemo({ body: cleanedPayload }).unwrap();
        else if (isLive) response = await createRequestLive({ body: cleanedPayload }).unwrap();
      }

      successRef.current = true;
      toast.success(response?.message || `Request ${type} submitted successfully`);
      navigate("/sales-leads");
    } catch (error) {
      const perSiteKeys = extractInvalidKeysPerSite(error?.data, sitesData);
      const nextErrors = Array(franchiseCount).fill({});
      let firstErrorTab = -1;
      perSiteKeys.forEach((keys, tabIndex) => {
        if (keys.length === 0) return;
        nextErrors[tabIndex] = buildInvalidKeyErrors(keys, error?.data?.message);
        if (firstErrorTab === -1) firstErrorTab = tabIndex;
      });
      setApiErrorsByTab(nextErrors);
      if (firstErrorTab !== -1) setCurrentTab(firstErrorTab);
      captureComponentError(error, { component: "FranchiseMultiTabPage", action: "handleSubmit" });
      toast.error(error?.data?.message || "Something went wrong");
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <LoadableContent isLoading={isLoading}>
      <nav className="flex bg-gray-100/50 p-1.5 rounded-xl w-fit border border-gray-200 mb-4">
        {Array.from({ length: franchiseCount }).map((_, i) => (
          <div
            key={i}
            className={[
              "relative px-8 py-3 text-sm font-bold rounded-xl transition-all duration-500 flex items-center justify-center gap-2.5 min-w-[140px]",
              currentTab === i
                ? "bg-white text-secondary-primary_button_hover shadow-[0_4px_12px_rgba(0,0,0,0.08)] scale-[1.02] cursor-default"
                : "text-gray-400 cursor-default",
            ].join(" ")}>
            Franchise {i + 1}
            {Object.keys(apiErrorsByTab[i] || {}).length > 0 && (
              <span className="w-2 h-2 rounded-full bg-red-500" title="This site has errors" />
            )}
          </div>
        ))}
      </nav>

      {Array.from({ length: franchiseCount }).map((_, i) => (
        <div key={i} className={i === currentTab ? "block" : "hidden"}>
          <RequestSiteConfigForm
            userInput={sitesData[i]}
            setUserInput={(updater) => updateSiteData(i, updater)}
            hideActions={true}
            externalIsSubmitted={tabsSubmitted[i]}
            isFranchise={isFranchise}
            showLogoFavicon={i === 0}
            showCompanyName={i === 0}
            idPrefix={`tab-${i}-`}
            apiErrors={apiErrorsByTab[i]}
            onUploadFile={async (base64, fileType) => {
              const res = await uploadFile({ base64, type: fileType }).unwrap();
              if (res?.status === "SUCCESS") return res.data?.url || res.data;
              throw new Error("Upload failed");
            }}
          />
        </div>
      ))}

      <InRow gap={1} classNames={["justify-center mt-8"]}>
        {currentTab > 0 && (
          <SecondaryButton disabled={uploadFileIsloading} classNames={["min-w-[130px]"]} action={handlePrev}>
            Previous
          </SecondaryButton>
        )}
        {!isEdit && (
          <SecondaryButton disabled={uploadFileIsloading || !isDraftEnabled} classNames={["min-w-[130px]"]} action={handleSaveDraft}>
            Save as Draft
          </SecondaryButton>
        )}
        {currentTab < franchiseCount - 1 ? (
          <PrimaryButton disabled={uploadFileIsloading} classNames={["min-w-[130px]"]} action={handleNext}>
            Next
          </PrimaryButton>
        ) : (
          <PrimaryButton disabled={uploadFileIsloading} classNames={["min-w-[130px]"]} action={handleSubmit}>
            Submit
          </PrimaryButton>
        )}
        <SecondaryButton disabled={uploadFileIsloading} classNames={["min-w-[130px]"]} action={() => navigate("/sales-leads")}>
          Cancel
        </SecondaryButton>
      </InRow>
    </LoadableContent>
  );
};

const Page = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const { data: apiResponse, isLoading: isLeadLoading, isError: isLeadError } = useGetSalesLeadsListQuery({ _id: id }, { skip: !id });
  const fetchedLead = apiResponse?.data?.find((item) => item._id === id) || null;

  const lead = id ? fetchedLead : state?.lead;

  const requestData = lead ? (type === "demo" ? lead?.requestDemo : lead?.requestLive) : null;
  const isFranchise = id
    ? (requestData?.[0]?.isFranchise ?? false)
    : (state?.isFranchise || false);
  const franchiseCount = id
    ? (requestData?.length || 1)
    : (state?.franchiseCount || 1);

  if (!id && !state?.lead) {
    navigate("/sales-leads");
    return null;
  }
  return (
    <CommonPage>
      <Content>
        <LoadableContent isLoading={isLeadLoading} isError={isLeadError || (!isLeadLoading && id && !lead)}>
          {lead && (
            isFranchise && franchiseCount > 0 ? (
              <FranchiseMultiTabPage lead={lead} type={type} franchiseCount={franchiseCount} isFranchise={isFranchise} />
            ) : (
              <SingleRequestForm
                lead={lead}
                type={type}
                isFranchise={isFranchise}
                index={0}
                isActive={true}
              />
            )
          )}
        </LoadableContent>
      </Content>
    </CommonPage>
  );
};

export default Page;
